import { ReactNode } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { Processor } from '@frontend/api-payments';
import { SetupFutureUsageElementsProp } from '@frontend/api-weave-pay';
import { stripeAppearance, PaymentConfigName, PaymentsFlowType } from '@frontend/payments-stripe-wrapper';
import { ContentLoader } from '@frontend/design-system';
import { useLoadStripeConfig } from '../../hooks/stripe/use-load-stripe-config';
import { useLoadStripeKey } from '../../hooks/stripe/use-load-stripe-key';
import { UseProcessorData } from '../../hooks/use-processor-data';

interface StripeElementsConfigProps {
  locationId: string;
  isPublic: boolean;
  enableAchOnFile: boolean;
  activeProcessor: Processor | undefined;
  locationData: UseProcessorData['locationData'];
  merchantIsFetched: boolean;
  type: PaymentsFlowType;
  children: ReactNode;
  mode?: 'payment' | 'setup';
  amount?: number;
  paymentConfigName?: PaymentConfigName;
  disableAch?: boolean;
}

export const StripeElementsConfig = ({
  mode = 'payment',
  amount = 50,
  type,
  children,
  paymentConfigName,
  disableAch,
  locationId,
  isPublic,
  enableAchOnFile,
  merchantIsFetched,
  activeProcessor,
  locationData,
}: StripeElementsConfigProps) => {
  const {
    stripePromise,
    stripeKey,
    isLoading: isLoadingLibrary,
  } = useLoadStripeKey({
    locationId,
    isPublic,
    opsType: locationData?.opsType,
  });

  const isAddCardOnFileFlow = type === PaymentsFlowType.AddCardOnFile;

  const setupFutureUsage = isAddCardOnFileFlow ? SetupFutureUsageElementsProp.OffSession : undefined;
  mode = isAddCardOnFileFlow ? 'setup' : mode;
  paymentConfigName = !disableAch && isAddCardOnFileFlow && enableAchOnFile ? 'cardAndAch' : paymentConfigName;

  const paymentMethodConfiguration = useLoadStripeConfig({
    paymentConfigName: paymentConfigName ?? 'cardOnly',
    isPublic,
    locationData,
  });

  const cardOnFilePaymentMethodOptions = { us_bank_account: { verification_method: 'instant' as const } };
  const isLoading = isLoadingLibrary || !merchantIsFetched || !stripeKey || paymentMethodConfiguration === null;

  //we want undefined value of paymentMethodConfiguration to be allowed to set the default config
  if (isLoading) {
    return <ContentLoader show={true} />;
  }

  const options: StripeElementsOptions = {
    mode,
    appearance: stripeAppearance,
    setupFutureUsage,
    currency: 'usd',
    paymentMethodConfiguration,
    onBehalfOf: activeProcessor?.stripeId,
    ...(mode !== 'setup' ? { amount } : {}),
    ...(isAddCardOnFileFlow ? { paymentMethodOptions: cardOnFilePaymentMethodOptions } : {}),
  };

  return (
    <Elements key={stripeKey} stripe={stripePromise} options={options}>
      {children}
    </Elements>
  );
};
